import { useEffect, useState } from "react";
import {
  DragEndEvent,
  DragOverEvent,
  Over,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  AdminAllLocationsCustomerResDto,
  AdminGetRoutesLocationsResDto,
  AdminGetRoutesResDto,
} from "service/api/estabrookSchemas";
import {
  useAdminRouteV2ControllerTransferLocationToRoute,
  useAdminRouteV2ControllerUpdateRouteCustomerOrders,
  useAdminRouteV2ControllerAddUnassignedLocationsToRoute,
} from "service";
import { RouteStatus } from "../pages/dashboard/route-planner/helpers/route-status.enum";
import { findContainer } from "../pages/dashboard/route-planner/helpers";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import useRoutePlannerStore, {
  Modals,
  MoveOrders,
  MoveOrdersLocationIds,
  PendingUnassignedMoveValues,
} from "../state/route-planner-store";

import {
  MapMarker,
  OrderStatus,
  OrderStatuses,
} from "../components/map/map.types";
import { generateToastMessageForTransfer } from "../pages/dashboard/route-planner/build/move-locations-dialog";
import { isUnassignedAddress } from "../shared/utils/address-assignment";
import { queryClient } from "../query-client";

export interface UseRoutePlannerDragStateProps {
  currentRouteStatus?: RouteStatus;
  refetchData?: () => void;
  routes: AdminGetRoutesResDto[];
}

export const useRoutePlannerDragState = ({
  currentRouteStatus,
  refetchData,
  routes: routesData,
}: UseRoutePlannerDragStateProps) => {
  const { date } = useParams();
  const selectedDate: DateTime = date
    ? DateTime.fromFormat(date, "MM-dd-yyyy")
    : DateTime.now();
  const [routesBeforeDrag, setRoutesBeforeDrag] = useState<
    AdminGetRoutesResDto[]
  >([]);
  const [startRouteId, setStartRouteId] = useState<string>("");
  const [startStatus, setStartStatus] = useState<string>("");
  const [shouldFilterSelected, setShouldFilterSelected] =
    useState<boolean>(true);
  const {
    setRoutes,
    selectedAddresses,
    moveOrders,
    setMoveOrders,
    setMoveDialog,
    setMoveOrdersDialogOpen,
    moveOrdersDialogOpen,
    resetAllSelected,
    validSequence,
    setIsLoadingModalShown,
    setSelectedAddressesForSequencing,
    setMapCentering,
    setActiveModal,
    setHasPlanningChanges,
    setPendingUnassignedMove,
  } = useRoutePlannerStore();

  useEffect(() => {
    if (!moveOrdersDialogOpen) {
      resetDragState();
    }
  }, [moveOrdersDialogOpen]);

  const showLoadingScreenWithTimeout = (): NodeJS.Timeout => {
    return setTimeout(() => {
      setIsLoadingModalShown(true);
    }, 250);
  };

  const clearTimeoutAndHideModal = (timeout: NodeJS.Timeout) => {
    clearTimeout(timeout);
    setIsLoadingModalShown(false);
  };

  const { mutateAsync: updateRouteCustomerOrders } =
    useAdminRouteV2ControllerUpdateRouteCustomerOrders({
      onSuccess: () => {
        toast.success("Route customer orders updated");
        setHasPlanningChanges(true);
        refetchData?.();
      },
    });

  const { mutateAsync: transferOrders } =
    useAdminRouteV2ControllerTransferLocationToRoute({
      onSuccess: () => {
        toast.success(generateToastMessageForTransfer(moveOrders));
        setHasPlanningChanges(true);
        refetchData?.();
        resetAllSelected();
      },
      onError: () => {
        setRoutes(routesBeforeDrag);
        resetDragState();
      },
    });

  const { mutateAsync: addUnassignedLocationsToRoute } =
    useAdminRouteV2ControllerAddUnassignedLocationsToRoute({
      // No success handler here - will be handled by the confirmation modal
      onError: () => {
        setRoutes(routesBeforeDrag);
        resetDragState();
      },
    });

  const changeOrderStatus = async (event: DragEndEvent, over: Over) => {
    const { active } = event;
    const activeItemId = active.id;
    const activeContainer = findContainer(activeItemId, routesData);

    // If drag was cancelled or no valid drop target, reset to original state
    if (!over) {
      if (routesBeforeDrag.length > 0) {
        setRoutes(routesBeforeDrag);
      }
      resetDragState();
      return;
    }

    const overItemId = over.id;
    const overContainer = findContainer(overItemId, routesData);
    const { currentItem, activeRouteId } = getCurrentRoutesAndItem(
      activeContainer ?? "",
      overContainer ?? "",
      activeItemId,
    );

    // Get the target route ID from the drop zone data
    const targetRouteId = over.data?.current?.routeId || activeRouteId;

    if (currentRouteStatus !== RouteStatus.Planning) {
      // Use the over item ID as the drop position reference
      await handleBuildAndLivePhase(overItemId, currentItem, targetRouteId);
    } else {
      await handlePlanPhase(currentItem, activeRouteId, targetRouteId);
    }

    resetDragState();
  };

  function moveOrderOnDragOver({
    active,
    over,
  }: DragOverEvent): AdminGetRoutesResDto[] | undefined {
    const activeItemId = active.id;
    const overItemId = over?.id;

    // Safety check: prevent processing if no valid over target
    if (!over || !overItemId) {
      return undefined;
    }

    // Find the containers
    const activeContainer = findContainer(activeItemId, routesData);
    const overContainer = findContainer(overItemId, routesData);

    if (!activeContainer) {
      return undefined; // Return undefined if can't find active container
    }

    // If no valid over container or dragging over the same container, don't update
    if (!overContainer || activeContainer === overContainer) {
      return undefined; // Return undefined to prevent state updates
    }

    const {
      currentRoutes,
      currentItem,
      activeRouteId,
      activeIndex,
      activeRouteItems,
      overRouteId,
      overRouteStatus,
    } = getCurrentRoutesAndItem(activeContainer, overContainer, activeItemId);

    if (routesBeforeDrag.length === 0) {
      setRoutesBeforeDrag(routesData);
      setStartRouteId(activeRouteId);
      setStartStatus(currentItem?.order?.status ?? "");
      setShouldFilterSelected(true);
    }

    if (currentItem === undefined) return undefined;

    // Prevent dragging TO the unassigned route (only allow dragging FROM it)
    if (
      overRouteId === "unassigned-route" &&
      activeRouteId !== "unassigned-route"
    ) {
      return undefined; // Keep original state, don't show drag preview
    }

    const itemToMove: AdminGetRoutesLocationsResDto = {
      ...currentItem,
      order: {
        ...currentItem?.order,
        status: overRouteStatus,
        customerSkipOverridden:
          currentItem?.order?.customerSkipOverridden ?? false,
      },
      locationId: currentItem?.locationId || "",
    };

    const filteredRoutes = filterRoutes(currentRoutes, itemToMove);

    if (activeIndex === undefined || !activeRouteItems) return undefined;

    return filteredRoutes.map((route) => {
      if (route.id === activeRouteId) {
        // If dragging over the same route
        if (activeRouteId === overRouteId) {
          const updatedLocations = route.locations?.reduce(
            (result, location) => {
              if (location.locationId === activeItemId) {
                result.itemToMoveToEnd = {
                  ...location,
                  order: {
                    ...location.order,
                    status: overRouteStatus,
                    customerSkipOverridden:
                      location.order?.customerSkipOverridden ?? false,
                  },
                };
              } else {
                result.locations.push(location);
              }
              return result;
            },
            {
              locations: [] as AdminGetRoutesLocationsResDto[],
              itemToMoveToEnd: null as AdminGetRoutesLocationsResDto | null,
            },
          );

          if (updatedLocations?.itemToMoveToEnd) {
            updatedLocations.locations.push(updatedLocations.itemToMoveToEnd);
          }

          return {
            ...route,
            locations: updatedLocations?.locations,
          };
        } else {
          // If dragging to a different route, remove the item
          return {
            ...route,
            locations: route.locations?.filter(
              (location) => location.locationId !== activeItemId,
            ),
          };
        }
      }

      // If dragging to a different route, add the item with updated status (only if not already there)
      if (route.id === overRouteId) {
        const existingItem = route.locations?.find(
          (location) => location.locationId === activeItemId,
        );
        if (existingItem) {
          // Item is already in the target route, just update its status
          return {
            ...route,
            locations: route.locations?.map((location) =>
              location.locationId === activeItemId
                ? {
                    ...location,
                    order: {
                      ...location.order,
                      status: overRouteStatus,
                      customerSkipOverridden:
                        location.order?.customerSkipOverridden ?? false,
                    },
                  }
                : location,
            ),
          };
        } else {
          // Item is not in the target route, add it
          return {
            ...route,
            locations: [...(route.locations ?? []), itemToMove],
          };
        }
      }

      return route;
    });
  }

  const removeSelectedAddressesOnDrag = (
    currentRoutes: AdminGetRoutesResDto[],
    currentMovingItem: AdminGetRoutesLocationsResDto,
  ): AdminGetRoutesResDto[] => {
    const selectedItemsToMove: AdminGetRoutesLocationsResDto[] = currentRoutes
      .flatMap((route) => route.locations ?? [])
      .filter((location) =>
        selectedAddresses.some(
          (marker) =>
            marker.id === location?.locationId &&
            marker.id !== currentMovingItem.locationId,
        ),
      );

    if (selectedItemsToMove.length === 0 || !shouldFilterSelected)
      return currentRoutes;

    setShouldFilterSelected(false);

    const updatedRoutes = currentRoutes.map((route) => {
      return {
        ...route,
        locations: route.locations?.filter(
          (location) =>
            !selectedItemsToMove.some(
              (item) => item.locationId === location.locationId,
            ),
        ),
      };
      return route;
    });

    return updatedRoutes;
  };

  const filterRoutes = (
    currentRoutes: AdminGetRoutesResDto[],
    currentMovingItem: AdminGetRoutesLocationsResDto,
  ): AdminGetRoutesResDto[] => {
    if (selectedAddresses.length > 1) {
      return removeSelectedAddressesOnDrag(currentRoutes, currentMovingItem);
    }
    return currentRoutes;
  };

  const resetDragState = () => {
    setStartRouteId("");
    setStartStatus("");
    setRoutesBeforeDrag([]);
    setShouldFilterSelected(true);
  };

  const getCurrentRoutesAndItem = (
    activeContainer: string,
    overContainer: string,
    activeItemId: UniqueIdentifier,
  ) => {
    const currentRoutes = [...routesData];
    const activeRouteId = activeContainer.slice(
      0,
      activeContainer.lastIndexOf("-"),
    );

    const overRouteId = overContainer.slice(0, overContainer.lastIndexOf("-"));
    const overRouteStatus = overContainer.slice(
      overContainer.lastIndexOf("-") + 1,
    ) as "skipped" | "regular" | "delayed" | "urgent";

    const activeRouteItems = currentRoutes.find(
      (route) => route.id === activeRouteId,
    )?.locations;
    const currentItem = activeRouteItems?.find(
      (item) => item.locationId === activeItemId,
    );
    const activeIndex = activeRouteItems?.findIndex(
      (item) => item.locationId === activeItemId,
    );

    return {
      currentRoutes,
      currentItem,
      activeRouteId,
      activeIndex,
      activeRouteItems,
      overRouteId,
      overRouteStatus,
    };
  };

  const handlePlanPhase = async (
    currentItem: AdminGetRoutesLocationsResDto | undefined,
    activeRouteId: string,
    targetRouteId?: string,
  ) => {
    // Prevent moving addresses TO the unassigned route
    if (
      targetRouteId === "unassigned-route" &&
      activeRouteId !== "unassigned-route"
    ) {
      if (routesBeforeDrag.length > 0) {
        setRoutes(routesBeforeDrag);
      }
      toast.error("Unassigned orders can only be moved out.");
      resetDragState();
      return;
    }

    const sameRoute =
      selectedAddresses.length > 1
        ? selectedAddresses.every(
            (address) => address.routeId === activeRouteId,
          )
        : startRouteId === activeRouteId;
    if (
      currentRouteStatus === RouteStatus.Planning &&
      routesBeforeDrag.length > 0 &&
      !sameRoute
    ) {
      setRoutes(routesBeforeDrag);
      toast.error(
        "Addresses can only be moved to shifts within the same Route.",
      );
      resetDragState();
      return;
    }

    if (startStatus === currentItem?.order?.status) {
      resetDragState();
      return;
    }
    resetDragState();
    if (
      currentItem?.locationId &&
      currentRouteStatus === RouteStatus.Planning
    ) {
      const loadingTimeout = showLoadingScreenWithTimeout();
      const locationIds: MoveOrdersLocationIds[] = selectedAddresses.map(
        (address) => ({
          locationId: address.id,
          seqNumber: 0,
        }),
      );
      const route = routesData.find(
        (route) => route.id === (targetRouteId || activeRouteId),
      );
      const moveOrders: MoveOrders = {
        targetRouteId: targetRouteId || activeRouteId,
        targetRouteName: route?.region?.name ?? route?.routeName ?? "",
        targetSeqNumber: 0,
        targetRouteOrderStatus: currentItem?.order?.status as
          | "skipped"
          | "regular"
          | "delayed"
          | "urgent",
        transferMode: "after",
        locationIds:
          selectedAddresses.length > 1
            ? locationIds
            : [
                {
                  locationId: currentItem.locationId,
                  seqNumber: 0,
                },
              ],
        visibleTargetSequence: 0,
      };

      setMoveOrders(moveOrders);

      // Check if this is a cross-route move (from unassigned to another route)
      const isCrossRouteMove =
        activeRouteId === "unassigned-route" &&
        targetRouteId &&
        targetRouteId !== activeRouteId;

      // Check if we're moving unassigned addresses to a route
      const isMovingUnassignedToRoute =
        selectedAddresses.length > 0 &&
        selectedAddresses.every(isUnassignedAddress) &&
        targetRouteId &&
        targetRouteId !== "unassigned-route";

      if (selectedAddresses.length > 1) {
        if (isMovingUnassignedToRoute) {
          // Show confirmation modal for moving unassigned addresses
          const pendingMove: PendingUnassignedMoveValues = {
            moveOrders,
            selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
            mutationFn: addUnassignedLocationsToRoute,
            onSuccess: () => {
              setHasPlanningChanges(true);
              resetAllSelected();
              refetchData?.(); // Invalidate route planner
              // Also invalidate unassigned locations query
              queryClient.invalidateQueries({
                queryKey: [
                  "adminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated",
                ],
              });
            },
          };
          setPendingUnassignedMove(pendingMove);
          setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
          clearTimeoutAndHideModal(loadingTimeout);
        } else {
          setMoveOrdersDialogOpen(true);
          clearTimeoutAndHideModal(loadingTimeout);
        }
      } else if (isCrossRouteMove || isMovingUnassignedToRoute) {
        // Show confirmation modal for single unassigned address moves
        const pendingMove: PendingUnassignedMoveValues = {
          moveOrders,
          selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
          mutationFn: addUnassignedLocationsToRoute,
          onSuccess: () => {
            setHasPlanningChanges(true);
            refetchData?.();
            resetAllSelected();
          },
        };
        setPendingUnassignedMove(pendingMove);
        setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
        clearTimeoutAndHideModal(loadingTimeout);
      } else {
        // Use update endpoint for same-route status changes
        try {
          await updateRouteCustomerOrders({
            body: {
              locations: [currentItem.locationId],
              status: currentItem?.order?.status as
                | "skipped"
                | "regular"
                | "delayed"
                | "urgent",
            },
            pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
          });
        } catch (error) {
          // Toast error is handled globally
        } finally {
          clearTimeoutAndHideModal(loadingTimeout);
        }
      }
    }
  };

  const handleBuildAndLivePhase = async (
    prevOrderId: UniqueIdentifier,
    currentItem: AdminGetRoutesLocationsResDto | undefined,
    targetRouteId: string,
  ) => {
    const prevItem = routesData
      .flatMap((route) => route.locations)
      .find((location) => location?.locationId === prevOrderId);

    if (currentItem) {
      // Prevent moving addresses TO the unassigned route
      const activeRouteId = routesData.find((route) =>
        route.locations?.some(
          (loc) => loc.locationId === currentItem.locationId,
        ),
      )?.id;

      if (
        targetRouteId === "unassigned-route" &&
        activeRouteId !== "unassigned-route"
      ) {
        if (routesBeforeDrag.length > 0) {
          setRoutes(routesBeforeDrag);
        }
        toast.error("Unassigned orders can only be moved out.");
        resetDragState();
        return;
      }

      const route = routesData.find((route) => route.id === targetRouteId);
      const locationIds: MoveOrdersLocationIds[] = [];
      const sortedSelectedAddresses = selectedAddresses.sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      );

      const { targetSeqNumber, visibleTargetSequence } =
        calculateTargetSequence(targetRouteId, currentItem, prevItem);
      let futureTargetSeqNumber = targetSeqNumber;

      // Skipped orders restored during a live shift have no valid sequence
      // of their own - require the admin to confirm a real position via the
      // Move Addresses modal instead of silently transferring on drop.
      const isRestoringSkippedOrderLive =
        currentRouteStatus === RouteStatus.Live &&
        startStatus === OrderStatuses.Skipped;

      sortedSelectedAddresses.forEach((address) => {
        locationIds.push({
          locationId: address.id,
          seqNumber: futureTargetSeqNumber++,
        });
      });

      const moveOrders: MoveOrders = {
        targetRouteId: targetRouteId,
        targetRouteName: route?.region?.name ?? route?.routeName ?? "",
        targetSeqNumber: targetSeqNumber,
        targetRouteOrderStatus: currentItem?.order?.status as
          | "skipped"
          | "regular"
          | "delayed"
          | "urgent",
        transferMode: "after",
        locationIds:
          selectedAddresses.length > 1
            ? locationIds
            : [
                {
                  locationId: currentItem.locationId,
                  seqNumber: targetSeqNumber,
                },
              ],
        visibleTargetSequence: visibleTargetSequence,
      };

      setMoveOrders(moveOrders);

      // Check if we're moving unassigned addresses to a route
      const isMovingUnassignedToRoute =
        selectedAddresses.length > 0 &&
        selectedAddresses.every(isUnassignedAddress) &&
        targetRouteId &&
        targetRouteId !== "unassigned-route";

      if (selectedAddresses.length > 1) {
        if (isMovingUnassignedToRoute) {
          // Show confirmation modal for moving multiple unassigned addresses
          const pendingMove: PendingUnassignedMoveValues = {
            moveOrders,
            selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
            mutationFn: addUnassignedLocationsToRoute,
            onSuccess: () => {
              setHasPlanningChanges(true);
              resetAllSelected();
              refetchData?.(); // Invalidate route planner
              // Also invalidate unassigned locations query
              queryClient.invalidateQueries({
                queryKey: [
                  "adminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated",
                ],
              });
            },
          };
          setPendingUnassignedMove(pendingMove);
          setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
        } else if (!validSequence || isRestoringSkippedOrderLive) {
          prepareOrdersForResequence(
            targetSeqNumber,
            targetRouteId,
            currentItem?.order?.status as
              | "skipped"
              | "regular"
              | "delayed"
              | "urgent",
          );
          setMoveDialog({ open: true, step: 1 });
        } else {
          setMoveOrdersDialogOpen(true);
        }
      } else {
        const loadingTimeout = showLoadingScreenWithTimeout();

        // Use the appropriate API based on whether we're dragging from unassigned
        const isDraggingFromUnassigned = startRouteId === "unassigned-route";

        if (isDraggingFromUnassigned || isMovingUnassignedToRoute) {
          // Show confirmation modal for single unassigned address moves
          const pendingMove: PendingUnassignedMoveValues = {
            moveOrders,
            selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
            mutationFn: addUnassignedLocationsToRoute,
            onSuccess: () => {
              setHasPlanningChanges(true);
              resetAllSelected();
              refetchData?.(); // Invalidate route planner
              // Also invalidate unassigned locations query
              queryClient.invalidateQueries({
                queryKey: [
                  "adminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated",
                ],
              });
            },
          };
          setPendingUnassignedMove(pendingMove);
          setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
          clearTimeoutAndHideModal(loadingTimeout);
        } else if (isRestoringSkippedOrderLive) {
          clearTimeoutAndHideModal(loadingTimeout);
          setMoveDialog({ open: true, step: 0 });
        } else {
          try {
            await transferOrders({
              body: {
                ...moveOrders,
                targetDate: selectedDate.toFormat("yyyy-MM-dd"),
              },
              pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
            });
          } catch (error) {
            if (
              (error as unknown as { httpStatus: string })?.httpStatus === "409"
            ) {
              setActiveModal(Modals.CANNOT_UPDATE_ROUTE);
            }
          } finally {
            clearTimeoutAndHideModal(loadingTimeout);
          }
        }
      }
    }
    return;
  };

  const prepareOrdersForResequence = (
    futureTargetSeqNumber: number,
    targetRouteId: string,
    shift: string,
  ) => {
    const targetRoute = routesData.find((route) => route.id === targetRouteId);

    if (!targetRoute) return;

    const targetRouteShiftLocations = targetRoute.locations?.filter(
      (loc) => loc.order?.status === shift,
    );

    let resequencedTargetRouteLocations: MapMarker[] | null = null;
    let mapMarkerLocation: MapMarker | null = null;

    resequencedTargetRouteLocations =
      targetRouteShiftLocations?.map((location) => {
        mapMarkerLocation = {
          ...location,
          id: location.locationId,
          latitude: location.latitude,
          longitude: location.longitude,
          order: location.order as OrderStatus,
          region: targetRoute.region,
          customer: location.customer as AdminAllLocationsCustomerResDto,
        };

        if (location.locationSequence >= futureTargetSeqNumber) {
          return {
            ...mapMarkerLocation,
            locationSequence:
              location.locationSequence + selectedAddresses.length,
          };
        }

        return mapMarkerLocation;
      }) || [];

    if (!resequencedTargetRouteLocations) return;

    const routeWithLocationsToResequence: MapMarker[] = [
      ...resequencedTargetRouteLocations,
      ...selectedAddresses.map((address) => ({
        ...address,
        locationSequence: null,
        region: {
          id: "unassigned",
          name: "Unassigned",
          color: "unassigned",
        },
        order: {
          ...address.order,
          status: "regular",
        } as OrderStatus,
      })),
    ];

    const uniqueLocations = Array.from(
      new Map(
        routeWithLocationsToResequence.map((item) => [item.id, item]),
      ).values(),
    );

    setMapCentering(undefined, routeWithLocationsToResequence);
    setSelectedAddressesForSequencing(uniqueLocations, futureTargetSeqNumber);
  };

  const calculateTargetSequence = (
    activeRouteId: string,
    currentItem: AdminGetRoutesLocationsResDto | undefined,
    prevItem: AdminGetRoutesLocationsResDto | undefined,
  ) => {
    // If there's no prevItem, we're dropping at the end or in an empty area
    if (!prevItem) {
      const routeSequences =
        routesData
          ?.find((route) => route?.id === activeRouteId)
          ?.locations?.filter(
            (location) =>
              location?.order?.status === currentItem?.order?.status,
          )
          .map((location) => location.locationSequence ?? 0) ?? [];
      if (routeSequences.length > 0) {
        const maxSeqNumber = Math.max(...routeSequences);
        return {
          targetSeqNumber: maxSeqNumber + 1,
          visibleTargetSequence: maxSeqNumber,
        };
      }
      return { targetSeqNumber: 1, visibleTargetSequence: 1 };
    }

    const prevTargetSequence = prevItem.locationSequence ?? 1;

    // If we're dropping on the same item we're dragging, put it at the end
    if (currentItem && currentItem.locationId === prevItem.locationId) {
      const routeSequences =
        routesData
          ?.find((route) => route?.id === activeRouteId)
          ?.locations?.filter(
            (location) =>
              location?.order?.status === currentItem?.order?.status,
          )
          .map((location) => location.locationSequence ?? 0) ?? [];
      if (routeSequences.length > 0) {
        const maxSeqNumber = Math.max(...routeSequences);
        return {
          targetSeqNumber: maxSeqNumber + 1,
          visibleTargetSequence: maxSeqNumber,
        };
      }
      return { targetSeqNumber: 1, visibleTargetSequence: 1 };
    }

    // Drop before the prevItem
    return {
      targetSeqNumber: prevTargetSequence,
      visibleTargetSequence:
        prevTargetSequence === 1 ? prevTargetSequence : prevTargetSequence - 1,
    };
  };

  return {
    changeOrderStatus,
    moveOrderOnDragOver,
    resetDragState,
    routesBeforeDrag,
  };
};
